/**
 * Role configuration for the application.
 *
 * Priority determines default route when user has multiple roles.
 * Lower number = higher priority.
 */
export const ROLE_CONFIG = {
  dev: {
    priority: 1,
    defaultRoute: '/',
    permissions: {
      canViewCreate: true,
      canViewSearch: true,
      canViewBakers: true,
      canViewCustomOrders: true,
      canViewSettings: true,
      canViewMerchantOrders: true,
      canConsolidateOrders: true,
      canViewAnalytics: true,
      canViewJobApplications: true,
      canDesignToppers: true,
    },
  },
  cashier: {
    priority: 2,
    defaultRoute: '/',
    permissions: {
      canViewCreate: true,
      canViewSearch: true,
      canViewBakers: false,
      canViewCustomOrders: false,
      canViewSettings: true,
      canViewMerchantOrders: false,
      canConsolidateOrders: false,
      canViewAnalytics: true,
      canViewJobApplications: true,
      canDesignToppers: true,
    },
  },
  manager: {
    priority: 3,
    defaultRoute: '/',
    permissions: {
      canViewCreate: true,
      canViewSearch: true,
      canViewBakers: true,
      canViewCustomOrders: true,
      canViewSettings: true,
      canViewMerchantOrders: false,
      canConsolidateOrders: false,
      canViewAnalytics: true,
      canViewJobApplications: true,
      canDesignToppers: true,
    },
  },
  baker: {
    priority: 4,
    defaultRoute: '/panaderos',
    permissions: {
      canViewCreate: false,
      canViewSearch: false,
      canViewBakers: true,
      canViewCustomOrders: false,
      canViewSettings: true,
      canViewMerchantOrders: false,
      canConsolidateOrders: false,
      canViewAnalytics: true,
      canViewJobApplications: true,
      canDesignToppers: true,
    },
  },
  ordering: {
    priority: 5,
    defaultRoute: '/merchant-orders',
    permissions: {
      canViewCreate: false,
      canViewSearch: false,
      canViewBakers: false,
      canViewCustomOrders: false,
      canViewSettings: true,
      canViewMerchantOrders: true,
      canConsolidateOrders: true,
      canViewAnalytics: true,
      canViewJobApplications: true,
      canDesignToppers: false,
    },
  },
  owner: {
    priority: 6,
    defaultRoute: '/merchant-orders',
    permissions: {
      canViewCreate: false,
      canViewSearch: false,
      canViewBakers: false,
      canViewCustomOrders: false,
      canViewSettings: true,
      canViewMerchantOrders: true,
      canConsolidateOrders: true,
      canViewAnalytics: true,
      canViewJobApplications: true,
      canDesignToppers: false,
    },
  },
};

/**
 * Get the default route for a user based on their roles.
 * Uses role priority - lower number = higher priority.
 *
 * @param {string[]} userRoles - Array of role names
 * @returns {string} The default route path
 */
export function getDefaultRoute(userRoles) {
  if (!userRoles || userRoles.length === 0) {
    return '/';
  }

  // Filter to known roles and sort by priority
  const sortedRoles = userRoles
    .filter(role => ROLE_CONFIG[role])
    .sort((a, b) => ROLE_CONFIG[a].priority - ROLE_CONFIG[b].priority);

  if (sortedRoles.length === 0) {
    return '/';
  }

  return ROLE_CONFIG[sortedRoles[0]].defaultRoute;
}

/**
 * Compute combined permissions for a user with multiple roles.
 * Permissions are OR'd together (if any role grants it, user has it).
 *
 * @param {string[]} userRoles - Array of role names
 * @returns {object} Combined permissions object
 */
export function getRolePermissions(userRoles) {
  const defaultPermissions = {
    canViewCreate: false,
    canViewSearch: false,
    canViewBakers: false,
    canViewCustomOrders: false,
    canViewSettings: false,
    canViewMerchantOrders: false,
    canConsolidateOrders: false,
    canViewAnalytics: false,
    canViewJobApplications: false,
    canDesignToppers: false,
  };

  if (!userRoles || userRoles.length === 0) {
    return defaultPermissions;
  }

  // OR all permissions together
  return userRoles.reduce((combined, role) => {
    const roleConfig = ROLE_CONFIG[role];
    if (!roleConfig) return combined;

    return {
      canViewCreate: combined.canViewCreate || roleConfig.permissions.canViewCreate,
      canViewSearch: combined.canViewSearch || roleConfig.permissions.canViewSearch,
      canViewBakers: combined.canViewBakers || roleConfig.permissions.canViewBakers,
      canViewCustomOrders: combined.canViewCustomOrders || roleConfig.permissions.canViewCustomOrders,
      canViewSettings: combined.canViewSettings || roleConfig.permissions.canViewSettings,
      canViewMerchantOrders: combined.canViewMerchantOrders || roleConfig.permissions.canViewMerchantOrders,
      canConsolidateOrders: combined.canConsolidateOrders || roleConfig.permissions.canConsolidateOrders,
      canViewAnalytics: combined.canViewAnalytics || roleConfig.permissions.canViewAnalytics,
      canViewJobApplications: combined.canViewJobApplications || roleConfig.permissions.canViewJobApplications,
      canDesignToppers: combined.canDesignToppers || roleConfig.permissions.canDesignToppers,
    };
  }, defaultPermissions);
}
